import { createFileRoute, redirect } from '@tanstack/react-router';
import { genCDN } from 'shared/cdn';
import { gql } from 'shared/__generated__';
import _ from 'lodash';
import { genMeta } from '../../utils/meta';
import EventUserView from 'web2/app/routes/event/compose/-event-user-view';
import { getSessionUser } from '../../actions/getSessionUser';

gql(/* GraphQL */ `
  fragment EventUserCore on User {
    id
    username
    customTheme {
      id
      name
      colors
    }
    customThemeId
    avatarPhotoFile {
      id
      url
      isFailed
      isUploaded
      name
      extension
      createdAt
    }
  }
`);

gql(/* GraphQL */ `
  fragment EventItem on Event {
    id
    title
    imageUrl
    description
    guestLimit
    descriptionLexical
    location
    date
    endDate
    url
    tags
    state
    customTheme {
      id
      name
      colors
    }
    settings {
      canInviteFriends
      unlistEvent
      inviteOnly
      revealDetailsWithRSVP
    }
    collaborators {
      id
      user {
        ...EventUserCore
      }
    }
    creator {
      ...EventUserCore
    }
    friends: attendees(first: 10, onlyFriends: true) {
      edges {
        node {
          ...EventUserCore
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
    attendees(first: 20, after: $after) {
      edges {
        node {
          ...EventUserCore
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
    comments {
      id
      content
      createdAt
      originUser {
        ...EventUserCore
      }
    }
    attendeesTotalCount
    attendeesFriendsCount
    myStatus
  }
`);

const EVENT_QUERY = gql(/* GraphQL */ `
  query Event($eventId: String!, $after: String) {
    event(eventId: $eventId) {
      ...EventItem
    }
  }
`);

export const Route = createFileRoute('/event/$eventId')({
  component: RouteComponent,
  staleTime: 60 * 5,
  head(ctx) {
    const data = ctx.loaderData as Awaited<ReturnType<typeof Route.loader>>;

    const event = data?.initialEventData as any;

    const image = genCDN(event?.imageUrl ?? '/pi-image-for-social.png', 70, 'webp');

    return genMeta({
      title: `Event | ${_.truncate(event?.title, { length: 60 }).toUpperCase()}`,
      description: _.truncate(event?.description || 'LEARN MORE', { length: 155 }).toUpperCase(),
      image,
      // TODO REMOVE WHEN EVENTS ARE LIVE
      other: [{ name: 'robots', content: 'noindex' }],
    });
  },
  beforeLoad: async () => {
    const user = await getSessionUser();
    if (user?.role !== 'ADMIN') {
      throw redirect({ to: '/' });
    }
  },
  loader: async ({ params, context }) => {
    const user = await getSessionUser();
    // if (!user) {
    //   throw redirect({ to: '/sign-in' });
    // }

    const { data } = await context.apolloClient.query({
      query: EVENT_QUERY,
      variables: { eventId: params.eventId },
    });

    const userCanEdit = user
      ? data?.event?.collaborators.some((collaborator) => collaborator.user.id === user.id)
      : false;

    if (data?.event) {
      if (data?.event?.state !== 'PUBLISHED' && !userCanEdit) {
        throw redirect({ to: '/not-found' });
      }

      return {
        eventId: params.eventId,
        initialEventData: data?.event,
        userCanEdit: !!userCanEdit,
      };
    }

    throw redirect({ to: '/not-found' });
  },
});

function RouteComponent() {
  const { initialEventData, userCanEdit } = Route.useLoaderData();

  return <EventUserView event={initialEventData} userCanEdit={!!userCanEdit} />;
}
